import { Game } from "./game/Game";
import { loadContent } from "./content/loader";
import { FONTS, IMAGE_FILES, SOUND_IDS } from "./game/assets";
import { IntroClip } from "./IntroClip";
import { bindTouchControls } from "./TouchControls";
import {
  Event,
  Key,
  Stage,
  audioReady,
  bindKeyStage,
  bindMouse,
  preload,
  startFrameLoop,
} from "./runtime";

const STAGE_WIDTH = 720;
const STAGE_HEIGHT = 480;

declare global {
  interface Window {
    /** Console-only: reveals the main-menu cheat toggle for this page load. */
    allyourcheatsarebelongtous(): string;
  }
}

const canvas = document.querySelector<HTMLCanvasElement>("#stage");
const viewport = document.querySelector<HTMLDivElement>("#viewport");
const boot = document.querySelector<HTMLDivElement>("#boot");
const label = document.querySelector<HTMLDivElement>("#label");
const barFill = document.querySelector<HTMLDivElement>("#barFill");
const touchControls = document.querySelector<HTMLDivElement>("#touchControls");
const joystick = document.querySelector<HTMLDivElement>("#joystick");
const joystickStick = document.querySelector<HTMLDivElement>("#joystickStick");
const specialAttack =
  document.querySelector<HTMLButtonElement>("#specialAttack");
if (
  !canvas ||
  !viewport ||
  !boot ||
  !label ||
  !barFill ||
  !touchControls ||
  !joystick ||
  !joystickStick ||
  !specialAttack
) {
  throw new Error("page shell missing");
}

const ctx = canvas.getContext("2d", { alpha: false });
if (!ctx) throw new Error("2d context unavailable");
ctx.imageSmoothingEnabled = false;

const stage = new Stage(STAGE_WIDTH, STAGE_HEIGHT);
stage.frameRate = 30;
Key.initialize(stage);
bindKeyStage(stage);
const pointerInput = bindMouse(canvas, stage);

function fitCanvas(): void {
  const scale = Math.min(
    window.innerWidth / STAGE_WIDTH,
    window.innerHeight / STAGE_HEIGHT,
  );
  viewport!.style.width = `${Math.floor(STAGE_WIDTH * scale)}px`;
  viewport!.style.height = `${Math.floor(STAGE_HEIGHT * scale)}px`;
}
window.addEventListener("resize", fitCanvas);
fitCanvas();

/**
 * Reproduces the SWF main timeline: frame 1 is the preloader, frame 2 is the
 * window Game.maxIntro counts 185 frames of (the sponsor logo animation is not
 * part of this port, so it carries the attribution card), and frame 3 is the
 * game proper.
 */
async function boot_(): Promise<void> {
  const [content] = await Promise.all([
    loadContent(),
    preload(
      { images: IMAGE_FILES, sounds: SOUND_IDS, fonts: FONTS },
      (loaded, total) => {
        barFill!.style.width = `${Math.round((loaded / total) * 100)}%`;
      },
    ),
  ]);

  label!.textContent = "Click to play";
  const { promise, resolve } = Promise.withResolvers<void>();
  const start = () => {
    boot!.removeEventListener("click", start);
    resolve();
  };
  boot!.addEventListener("click", start);
  await promise;

  audioReady();
  boot!.classList.add("hidden");

  const game = new Game(content, pointerInput);
  game.totalFrames = 4;
  stage.addChild(game);
  pointerInput.setStagePressHandler((stageX, stageY) => {
    game.pointerDown(stageX, stageY);
  });
  window.allyourcheatsarebelongtous = () => {
    game.unlockCheatsForSession();
    return "ALL YOUR CHEATS ARE BELONG TO US - the CHEATS toggle is now in the main menu";
  };
  bindTouchControls(
    touchControls!,
    joystick!,
    joystickStick!,
    specialAttack!,
    pointerInput,
  );

  const intro = new IntroClip();
  let introRunning = false;

  game.frameScripts[2] = () => {
    game.addChild(intro);
    introRunning = true;
  };
  game.frameScripts[3] = () => {
    if (intro.parent) intro.parent.removeChild(intro);
    introRunning = false;
  };

  stage.addEventListener(Event.ENTER_FRAME, () => {
    if (introRunning) intro.advance();
  });

  startFrameLoop(stage, ctx!, "#000000");
  // the SWF preloader called root.play() once bytes were in; assets are local
  // here, so the timeline steps straight to the attribution card.
  game.gotoAndStop(2);
}

void boot_();
